import { useState } from 'react';
import { useNavigate, useMatches } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useShortcuts } from '@/shared/hooks/use-shortcuts';
import { useCommandPaletteStore } from '@/store/command-palette';
import { Cheatsheet } from '@/app/cheatsheet';

/**
 * App-wide keyboard shortcuts. Mounted once from <AppLayout>. Layout owns
 * `⌘K` directly because it should fire even when an input has focus; this
 * component owns the rest.
 *
 * `r` (refresh) calls TanStack Query's `refetchQueries({ type: 'active' })`,
 * which is approximately "refetch every query feeding the visible page" — the
 * same scope a per-page refresh button would cover, without each page having
 * to register its own queryKey.
 *
 * `n` (new) reads `handle.newPath` from the current matched route. Routes
 * that should respond to `n` declare a `newPath: '/students/new'` next to
 * their `crumb` in `router/index.tsx`.
 */
export function AppShortcuts() {
  const navigate = useNavigate();
  const matches = useMatches();
  const queryClient = useQueryClient();
  const openPalette = useCommandPaletteStore((s) => s.open);
  const { message } = App.useApp();

  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);

  // newPath from the deepest matched route, if any.
  const newPath = (() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const h = matches[i].handle as { newPath?: string } | undefined;
      if (h?.newPath) return h.newPath;
    }
    return undefined;
  })();

  useShortcuts([
    {
      keys: '?',
      group: 'General',
      description: 'Show shortcut cheatsheet',
      handler: () => setCheatsheetOpen(true),
    },
    {
      keys: 'b',
      group: 'General',
      description: 'Back',
      handler: () => window.history.back(),
    },
    {
      keys: 'r',
      group: 'General',
      description: 'Refresh current page data',
      handler: () => {
        void queryClient.refetchQueries({ type: 'active' });
        message.success({ content: 'Refreshing…', duration: 1.2 });
      },
    },
    {
      keys: 'n',
      group: 'General',
      description: 'New record',
      handler: () => {
        if (!newPath) {
          message.info({ content: 'No "new" action on this page', duration: 1.4 });
          return;
        }
        navigate(newPath);
      },
    },
    {
      keys: '/',
      group: 'General',
      description: 'Open command palette',
      handler: () => openPalette(),
    },
    // Jump-to chords ("g" then a section letter).
    { keys: 'g d', group: 'Jump to', description: 'Dashboard',  handler: () => navigate('/dashboard') },
    { keys: 'g s', group: 'Jump to', description: 'Students',   handler: () => navigate('/students') },
    { keys: 'g f', group: 'Jump to', description: 'Fees',       handler: () => navigate('/fee') },
    { keys: 'g a', group: 'Jump to', description: 'Attendance', handler: () => navigate('/attendance') },
    { keys: 'g h', group: 'Jump to', description: 'HR',         handler: () => navigate('/hr') },
    { keys: 'g x', group: 'Jump to', description: 'Finance',    handler: () => navigate('/finance') },
    { keys: 'g r', group: 'Jump to', description: 'Reports',    handler: () => navigate('/reports') },
    { keys: 'g o', group: 'Jump to', description: 'Front desk', handler: () => navigate('/frontdesk') },
    { keys: 'g i', group: 'Jump to', description: 'Inventory',  handler: () => navigate('/inventory') },
    { keys: 'g c', group: 'Jump to', description: 'Academics',  handler: () => navigate('/academics') },
  ]);

  return <Cheatsheet open={cheatsheetOpen} onClose={() => setCheatsheetOpen(false)} />;
}
